import React from 'react';
import customshineIcon from '../public/customshine.png';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Box,
  Typography,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Folder as FolderIcon,
  Extension as ModulesIcon,
  Category as CategoryIcon,
  Business as BusinessIcon,
  LibraryBooks as ResourcesIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import { useAuth } from '../auth/auth.context';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import ColorModeSelect from '../theme/ColorModeSelect';

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  // { text: 'Company Management', icon: <BusinessIcon />, path: '/companies' },
  { text: 'User Management', icon: <ManageAccountsIcon />, path: '/users' },
  { text: 'File Management', icon: <FolderIcon />, path: '/files' },
  { text: 'Course Management', icon: <CategoryIcon />, path: '/courses' },
  { text: 'Module Management', icon: <ModulesIcon />, path: '/modules' },
  { text: 'Resources Management', icon: <ResourcesIcon />, path: '/resources' },
];

function Sidebar({ drawerWidth, collapsedWidth, mobileOpen, isCollapsed, onDrawerToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const colorModeSelectRef = React.useRef(null);

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) {
      onDrawerToggle();
    }
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
    handleClose();
  };

  const getUserDisplayName = () => {
    if (currentUser?.displayName) {
      return currentUser.displayName;
    }
    if (currentUser?.email) {
      return currentUser.email.split('@')[0];
    }
    return 'Admin';
  };

  const getUserInitial = () => {
    const displayName = getUserDisplayName();
    return displayName.charAt(0).toUpperCase();
  };

  const currentWidth = isCollapsed ? collapsedWidth : drawerWidth;

  const drawer = (
    <Box sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar sx={{
        pl: 2,
        pr: 2,
        position: 'relative',
        justifyContent: 'flex-start',
        minHeight: 64
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minWidth: 0,
        }}>
          <img src={customshineIcon} alt="Customshine" style={{ width: 30, height: 30, flexShrink: 0 }} />
          <Box sx={{
            height: 28,
            color: 'text.primary',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            transition: 'opacity 0.3s, width 0.3s',
            whiteSpace: 'nowrap'
          }}>
            {/* <svg
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 325 49"
              xmlSpace="preserve"
              style={{ height: '90%', width: 'auto', fill: 'currentColor' }}
            >
              <g>
                <path
                  d="M21.998,0.728L0,48.256h4.711L23.887,6.235l19.121,42.021h4.904L25.943,0.728H21.998z M51.19,48.256h4.469V0.728H51.19V48.256z M288.098,0.728l1.944,4.191h14.272v43.309h4.469V4.919h14.272L325,0.728 C325,0.728,288.098,0.728,288.098,0.728z M127.107,22.261L127.107,22.261c-8.52-1.755-13.147-3.809-13.147-9.671v-0.128 c0-4.794,4.575-8.274,10.847-8.274c5.009-0.092,9.862,1.742,13.559,5.123l0.384,0.355l2.844-3.535l-0.384-0.329 c-4.529-3.855-10.324-5.897-16.269-5.733c-8.82,0-15.451,5.485-15.451,12.739v0.136c0,7.341,4.656,11.286,16.08,13.588 c8.274,1.672,12.739,3.644,12.739,9.395v0.136c0,5.15-4.629,8.735-11.258,8.735c-6.15,0.136-12.075-2.32-16.326-6.766l-0.384-0.355 l-2.931,3.37l0.384,0.329c5.112,4.932,11.963,7.649,19.066,7.561c9.369,0,15.916-5.424,15.916-13.204v-0.134 c-0.002-7.089-4.686-11.093-15.67-13.34V22.261z M191.619,0.728L173.21,27.847L154.8,0.728h-4.026v47.534h4.329V8.974 l18.024,26.239l18.024-26.239v39.283h4.469V0.728H191.619z M220.929,0.728l-21.998,47.528h4.711l19.198-41.994l19.121,41.994h4.904 L224.9,0.728H220.929z M283.901,48.262l-17.969-23.338h1.562c2.455,0.065,4.909-0.156,7.313-0.658 c5.013-1.343,8.026-4.766,8.026-9.505v-0.173c0-6.053-4.794-9.669-12.848-9.669h-15.367v43.309h-4.469V0.728H270.2 c4.933-0.255,9.766,1.449,13.45,4.739c2.356,2.397,3.674,5.624,3.671,8.985v0.136c0,7.205-4.904,12.245-13.204,13.669 l15.477,19.997L283.901,48.262z M99.133,48.262L81.167,24.916h1.562c2.455,0.065,4.909-0.156,7.313-0.658 c5.013-1.343,8.026-4.766,8.026-9.505v-0.165c0-6.053-4.794-9.669-12.848-9.669H69.853v43.309h-4.469V0.728h20.052 c4.933-0.255,9.766,1.449,13.45,4.739c2.358,2.396,3.679,5.623,3.677,8.985v0.136c0,7.205-4.904,12.245-13.204,13.669 l15.477,19.997L99.133,48.262z"
                  fill="currentColor"
                />
              </g>
            </svg> */}
          </Box>
        </Box>
      </Toolbar>
      <Divider />
      <List sx={{ px: 1, flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ display: 'block', mb: 0.5 }}>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                minHeight: 48,
                justifyContent: 'flex-start',
                px: 1.9,
                borderRadius: 1,
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'primary.main',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'primary.main',
                    fontWeight: 'bold',
                  },
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: 3,
                  justifyContent: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  transition: 'none'
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  opacity: isCollapsed ? 0 : 1,
                  width: isCollapsed ? 0 : 'auto',
                  transition: 'opacity 0.3s',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden'
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* User Profile Section at Bottom */}
      <Divider />
      <Box sx={{ p: 2, display: { xs: 'none', sm: 'block' } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            cursor: 'pointer',
            p: 1,
            borderRadius: 1,
            '&:hover': { bgcolor: 'action.hover' },
            width: '100%'
          }}
          onClick={handleMenu}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: '0.875rem', flexShrink: 0 }}>
            {getUserInitial()}
          </Avatar>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            opacity: isCollapsed ? 0 : 1,
            width: isCollapsed ? 0 : 'auto',
            transition: 'opacity 0.3s, width 0.3s',
            overflow: 'hidden',
            whiteSpace: 'nowrap'
          }}>
            <Typography variant="body2" fontWeight="bold">
              {getUserDisplayName()}
            </Typography>
          </Box>
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          PaperProps={{
            sx: {
              width: 220,
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRadius: 1,
              boxShadow: 'none',
              border: '1px solid',
              borderColor: 'divider',
              mb: 1,
            }
          }}
        >
          <MenuItem
            onClick={(e) => {
              // Trigger click on the ColorModeSelect button
              const button = colorModeSelectRef.current?.querySelector('button');
              if (button) {
                button.click();
              }
            }}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              py: 1,
            }}
          >
            <Typography variant="body2">Theme</Typography>
            <Box
              ref={colorModeSelectRef}
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 20,
                '& button': {
                  padding: 0,
                  minWidth: 'auto',
                  height: 20,
                },
                '& svg': {
                  fontSize: '1.25rem',
                }
              }}
            >
              <ColorModeSelect size="small" />
            </Box>
          </MenuItem>
          <MenuItem
            onClick={handleLogout}
            sx={{
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 1,
            }}
          >
            <Typography variant="body2">Logout</Typography>
            <LogoutIcon sx={{ fontSize: '1.25rem' }} />
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: currentWidth },
        flexShrink: { sm: 0 },
        transition: (theme) => theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        ModalProps={{
          keepMounted: true,
          disableScrollLock: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: currentWidth,
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Sidebar;